import React, { useState, useMemo } from 'react';
import SchemaFieldRenderer from 'components/schema/SchemaFieldRenderer';
import LineItemMatchingCriteria from './LineItemMatchingCriteria';
import styles from './BulkUpdateModal.module.css';

const LINE_ITEM_UPDATE_MODES = {
  ALL_ITEMS: 'all_items',
  FIRST_ITEM: 'first_item',
  MATCHING_CRITERIA: 'matching',
  ADD_NEW_ITEM: 'add_new'
};

function BulkUpdateModal({ 
  isOpen, 
  selectedRecords, 
  recordType, 
  schema, 
  lookupData, 
  onUpdate, 
  onCancel 
}) {
  const [updates, setUpdates] = useState({});
  const [lineItemUpdates, setLineItemUpdates] = useState({});
  const [updateMode, setUpdateMode] = useState('record_fields');
  const [lineItemMode, setLineItemMode] = useState(LINE_ITEM_UPDATE_MODES.ALL_ITEMS);
  const [matchingCriteria, setMatchingCriteria] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 3;

  // Get editable record-level fields
  const editableFields = useMemo(() => {
    if (!schema) return [];
    return Object.entries(schema)
      .filter(([fieldName, fieldDef]) => 
        fieldDef.editable && 
        !fieldName.startsWith('_') && 
        fieldName !== 'line_items' &&
        fieldName !== 'extends' &&
        fieldName !== 'type'
      )
      .sort(([,a], [,b]) => (a.display_order || 999) - (b.display_order || 999));
  }, [schema]);

  // Get line item schema
  const lineItemSchema = schema?.line_items?.fields || {};
  const hasLineItems = Object.keys(lineItemSchema).length > 0;

  const getDefaultFieldValue = (fieldDef) => {
    switch (fieldDef.type) {
      case 'string': return '';
      case 'number': return 0;
      case 'boolean': return false;
      case 'lookup': return null;
      default: return '';
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setUpdates(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleLineItemFieldChange = (fieldName, value) => {
    setLineItemUpdates(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onUpdate({
        recordUpdates: updates,
        lineItemUpdates: Object.keys(lineItemUpdates).length > 0 ? {
          updates: lineItemUpdates,
          mode: lineItemMode,
          matchingCriteria: matchingCriteria
        } : null
      });
      setCurrentStep(3);
    } catch (error) {
      console.error('Update failed:', error);
      // Stay on step 2 to show error
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = Object.keys(updates).length > 0 || Object.keys(lineItemUpdates).length > 0;

  // Calculate preview of changes for each record
  const calculateImpactPreview = () => {
    return selectedRecords.map(record => {
      const changes = {};
      const lineItemChanges = [];
      
      // Record-level changes
      Object.entries(updates).forEach(([field, value]) => {
        if (record[field] !== value) {
          changes[field] = { from: record[field], to: value };
        }
      });

      // Line item changes
      if (Object.keys(lineItemUpdates).length > 0) {
        const currentLineItems = record.rec_normalized?.line_items || [];
        
        switch (lineItemMode) {
          case LINE_ITEM_UPDATE_MODES.ALL_ITEMS:
            currentLineItems.forEach((item, index) => {
              const itemChanges = {};
              Object.entries(lineItemUpdates).forEach(([field, value]) => {
                if (item[field] !== value) {
                  itemChanges[field] = { from: item[field], to: value };
                }
              });
              if (Object.keys(itemChanges).length > 0) {
                lineItemChanges.push({ index, changes: itemChanges });
              }
            });
            break;
            
          case LINE_ITEM_UPDATE_MODES.FIRST_ITEM:
            if (currentLineItems.length > 0) {
              const itemChanges = {};
              Object.entries(lineItemUpdates).forEach(([field, value]) => {
                if (currentLineItems[0][field] !== value) {
                  itemChanges[field] = { from: currentLineItems[0][field], to: value };
                }
              });
              if (Object.keys(itemChanges).length > 0) {
                lineItemChanges.push({ index: 0, changes: itemChanges });
              }
            }
            break;
            
          case LINE_ITEM_UPDATE_MODES.MATCHING_CRITERIA:
            currentLineItems.forEach((item, index) => {
              const matches = Object.entries(matchingCriteria).every(([field, value]) => {
                if (value === '' || value === null || value === undefined) return true;
                return item[field] === value;
              });
              if (matches) {
                const itemChanges = {};
                Object.entries(lineItemUpdates).forEach(([field, value]) => {
                  if (item[field] !== value) {
                    itemChanges[field] = { from: item[field], to: value };
                  }
                });
                if (Object.keys(itemChanges).length > 0) {
                  lineItemChanges.push({ index, changes: itemChanges });
                }
              }
            });
            break;
            
          case LINE_ITEM_UPDATE_MODES.ADD_NEW_ITEM:
            lineItemChanges.push({ 
              index: 'new', 
              changes: Object.fromEntries(
                Object.entries(lineItemUpdates).map(([field, value]) => [field, { from: null, to: value }])
              )
            });
            break;
            
          default:
            break;
        }
      }

      return {
        record,
        recordChanges: changes,
        lineItemChanges
      };
    }).filter(preview => 
      Object.keys(preview.recordChanges).length > 0 || preview.lineItemChanges.length > 0
    );
  };

  const renderStepIndicator = () => (
    <div className={styles.stepIndicator}>
      <div className={styles.steps}>
        <div className={`${styles.step} ${currentStep >= 1 ? styles.active : ''} ${currentStep > 1 ? styles.completed : ''}`}>
          <span className={styles.stepNumber}>1</span>
          <span className={styles.stepLabel}>Select Updates</span>
        </div>
        <div className={styles.stepConnector}></div>
        <div className={`${styles.step} ${currentStep >= 2 ? styles.active : ''} ${currentStep > 2 ? styles.completed : ''}`}>
          <span className={styles.stepNumber}>2</span>
          <span className={styles.stepLabel}>Review Impact</span>
        </div>
        <div className={styles.stepConnector}></div>
        <div className={`${styles.step} ${currentStep >= 3 ? styles.active : ''}`}>
          <span className={styles.stepNumber}>3</span>
          <span className={styles.stepLabel}>Confirmation</span>
        </div>
      </div>
    </div>
  );

  const renderImpactPreview = () => {
    const impactPreview = calculateImpactPreview();
    
    return (
      <div className={styles.impactPreview}>
        <div className={styles.previewHeader}>
          <h3>Impact Preview</h3>
          <p>Review the changes that will be applied to {selectedRecords.length} records:</p>
        </div>
        
        {impactPreview.length === 0 ? (
          <div className={styles.noChanges}>
            <p>No changes detected. Please go back and select fields to update.</p>
          </div>
        ) : (
          <div className={styles.changesContainer}>
            <div className={styles.summaryStats}>
              <div className={styles.stat}>
                <strong>{impactPreview.length}</strong> records will be modified
              </div>
              <div className={styles.stat}>
                <strong>{impactPreview.reduce((acc, p) => acc + Object.keys(p.recordChanges).length, 0)}</strong> record field changes
              </div>
              <div className={styles.stat}>
                <strong>{impactPreview.reduce((acc, p) => acc + p.lineItemChanges.length, 0)}</strong> line item changes
              </div>
            </div>
            
            <div className={styles.changesList}>
              {impactPreview.slice(0, 5).map((preview, index) => (
                <div key={preview.record.id} className={styles.recordPreview}>
                  <div className={styles.recordHeader}>
                    <strong>Record {index + 1}:</strong> {preview.record.id}
                  </div>
                  
                  {Object.keys(preview.recordChanges).length > 0 && (
                    <div className={styles.recordChanges}>
                      <h5>Record Changes:</h5>
                      {Object.entries(preview.recordChanges).map(([field, change]) => (
                        <div key={field} className={styles.changeItem}>
                          <span className={styles.fieldName}>{field}:</span>
                          <span className={styles.changeValue}>
                            <span className={styles.oldValue}>{String(change.from || 'empty')}</span>
                            <span className={styles.arrow}>→</span>
                            <span className={styles.newValue}>{String(change.to)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {preview.lineItemChanges.length > 0 && (
                    <div className={styles.lineItemChanges}>
                      <h5>Line Item Changes:</h5>
                      {preview.lineItemChanges.map((lineItemChange, liIndex) => (
                        <div key={liIndex} className={styles.lineItemChange}>
                          <span className={styles.lineItemIndex}>
                            {lineItemChange.index === 'new' ? 'New Item' : `Item ${lineItemChange.index + 1}`}:
                          </span>
                          {Object.entries(lineItemChange.changes).map(([field, change]) => (
                            <div key={field} className={styles.changeItem}>
                              <span className={styles.fieldName}>{field}:</span>
                              <span className={styles.changeValue}>
                                <span className={styles.oldValue}>{String(change.from || 'empty')}</span>
                                <span className={styles.arrow}>→</span>
                                <span className={styles.newValue}>{String(change.to)}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {impactPreview.length > 5 && (
                <div className={styles.moreRecords}>
                  ... and {impactPreview.length - 5} more records
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConfirmation = () => (
    <div className={styles.confirmationScreen}>
      <div className={styles.confirmationIcon}>
        <div className={styles.successCheck}>✓</div>
      </div>
      <div className={styles.confirmationContent}>
        <h3>Update Submitted Successfully!</h3>
        <p>
          Changes have been pushed to the servers and are processing in the background.
        </p>
        <div className={styles.confirmationSummary}>
          <span><strong>{selectedRecords.length}</strong> records updating</span>
          {Object.keys(lineItemUpdates).length > 0 && (
            <span>• Line items included</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderRecordFieldsTab = () => (
    <div className={styles.recordFields}>
      <p className={styles.tabDescription}>
        Select fields to update across all selected {recordType} records:
      </p>
      {editableFields.length === 0 ? (
        <div className={styles.noFields}>
          No editable fields available for {recordType} records.
        </div>
      ) : (
        editableFields.map(([fieldName, fieldDef]) => (
          <div key={fieldName} className={styles.fieldRow}>
            <label className={styles.fieldLabel}>
              <input
                type="checkbox"
                checked={updates.hasOwnProperty(fieldName)}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleFieldChange(fieldName, getDefaultFieldValue(fieldDef));
                  } else {
                    const newUpdates = { ...updates };
                    delete newUpdates[fieldName];
                    setUpdates(newUpdates);
                  }
                }}
              />
              {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            
            {updates.hasOwnProperty(fieldName) && (
              <div className={styles.fieldEditor}>
                <SchemaFieldRenderer
                  fieldSchema={fieldDef}
                  fieldName={fieldName}
                  value={updates[fieldName]}
                  onChange={(value) => handleFieldChange(fieldName, value)}
                  isEditing={true}
                  lookupData={lookupData}
                  className={styles.bulkFieldInput}
                />
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderLineItemsTab = () => (
    <div className={styles.lineItemsSection}>
      <div className={styles.updateModeSelector}>
        <label>Update Strategy:</label>
        <select 
          value={lineItemMode} 
          onChange={(e) => setLineItemMode(e.target.value)}
          className={styles.modeSelect}
        >
          <option value={LINE_ITEM_UPDATE_MODES.ALL_ITEMS}>
            Update all line items in each record
          </option>
          <option value={LINE_ITEM_UPDATE_MODES.FIRST_ITEM}>
            Update only the first line item in each record
          </option>
          <option value={LINE_ITEM_UPDATE_MODES.MATCHING_CRITERIA}>
            Update line items matching criteria
          </option>
          <option value={LINE_ITEM_UPDATE_MODES.ADD_NEW_ITEM}>
            Add new line item to each record
          </option>
        </select>
      </div>

      {lineItemMode === LINE_ITEM_UPDATE_MODES.MATCHING_CRITERIA && (
        <LineItemMatchingCriteria
          schema={lineItemSchema}
          criteria={matchingCriteria}
          onChange={setMatchingCriteria}
          lookupData={lookupData}
        />
      )}

      <div className={styles.lineItemFields}>
        <h4>Fields to Update:</h4>
        {Object.entries(lineItemSchema).map(([fieldName, fieldDef]) => (
          <div key={fieldName} className={styles.fieldRow}>
            <label className={styles.fieldLabel}>
              <input
                type="checkbox"
                checked={lineItemUpdates.hasOwnProperty(fieldName)}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleLineItemFieldChange(fieldName, getDefaultFieldValue(fieldDef));
                  } else {
                    const newUpdates = { ...lineItemUpdates };
                    delete newUpdates[fieldName];
                    setLineItemUpdates(newUpdates);
                  }
                }}
              />
              {fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            
            {lineItemUpdates.hasOwnProperty(fieldName) && (
              <div className={styles.fieldEditor}>
                <SchemaFieldRenderer
                  fieldSchema={fieldDef}
                  fieldName={fieldName}
                  value={lineItemUpdates[fieldName]}
                  onChange={(value) => handleLineItemFieldChange(fieldName, value)}
                  isEditing={true}
                  lookupData={lookupData}
                  className={styles.bulkFieldInput}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Bulk Update {selectedRecords.length} {recordType} Records</h2>
          <button onClick={onCancel} className={styles.closeButton}>×</button>
        </div>
        
        {renderStepIndicator()}
        
        {currentStep === 1 && (
          <>
            <div className={styles.tabs}>
              <button 
                className={updateMode === 'record_fields' ? styles.activeTab : styles.tab}
                onClick={() => setUpdateMode('record_fields')}
              >
                Record Fields
              </button>
              {hasLineItems && (
                <button 
                  className={updateMode === 'line_items' ? styles.activeTab : styles.tab}
                  onClick={() => setUpdateMode('line_items')}
                >
                  Line Items ({Object.keys(lineItemSchema).length} fields)
                </button>
              )}
            </div>
            
            <div className={styles.content}>
              {updateMode === 'record_fields' ? renderRecordFieldsTab() : renderLineItemsTab()}
            </div>
          </>
        )}
        
        {currentStep === 2 && (
          <div className={styles.content}>
            {renderImpactPreview()}
          </div>
        )}
        
        {currentStep === 3 && (
          <div className={styles.content}>
            {renderConfirmation()}
          </div>
        )}
        
        <div className={styles.footer}>
          {currentStep < 3 && (
            <button onClick={onCancel} className={styles.cancelButton}>
              Cancel
            </button>
          )}
          
          {currentStep === 1 && (
            <button 
              onClick={handleNext}
              disabled={!hasChanges}
              className={styles.nextButton}
            >
              Next: Review Impact
            </button>
          )}
          
          {currentStep === 2 && (
            <>
              <button onClick={handleBack} className={styles.backButton}>
                Back
              </button>
              <button 
                onClick={handleSubmit}
                disabled={!hasChanges || isSubmitting}
                className={styles.updateButton}
              >
                {isSubmitting ? 'Submitting...' : `Confirm Update ${selectedRecords.length} Records`}
              </button>
            </>
          )}
          
          {currentStep === 3 && (
            <button 
              onClick={onCancel}
              className={styles.updateButton}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkUpdateModal;
export { LINE_ITEM_UPDATE_MODES };